/* eslint-disable jsx-a11y/no-static-element-interactions */
/* eslint-disable jsx-a11y/click-events-have-key-events */
/**
 * Chase-styled credit-card application prototype used as the v0DemoUrl
 * target for the Credit Card Application Streamlining demo experiment.
 * Self-hosted (same origin) so it iframes cleanly inside the prototype
 * preview pane. Public route — see AppRouter.
 */

import React from "react";

import { useLocation, useNavigate } from "react-router-dom";

const CHASE_BLUE = "#117ACA";
const CHASE_DARK = "#0A4F8B";
const CHASE_TEXT = "#1B1B1B";
const CHASE_MUTED = "#6B7280";
const CHASE_BG = "#F6F7F9";
const CHASE_BORDER = "#E5E7EB";

type StepKey = 1 | 2 | 3 | 4 | 5;

const STEPS: Array<{ key: StepKey; label: string }> = [
  { key: 1, label: "Goal" },
  { key: 2, label: "Verify" },
  { key: 3, label: "Offer" },
  { key: 4, label: "Terms" },
  { key: 5, label: "Done" },
];

const goalCards = [
  {
    id: "rewards",
    title: "Earn Rewards",
    description:
      "Travel points, cash back, and exclusive Sapphire perks on every purchase.",
    badge: "Most popular",
    recommended: "Sapphire Preferred",
  },
  {
    id: "build",
    title: "Build Credit",
    description:
      "Establish or rebuild your credit history with a simple, transparent card.",
    badge: null,
    recommended: "Slate Edge",
  },
  {
    id: "consolidate",
    title: "Consolidate Debt",
    description: "Move balances at a low intro APR and pay them down faster.",
    badge: null,
    recommended: "Slate Edge",
  },
  {
    id: "purchase",
    title: "Large Purchase",
    description:
      "Spread a larger purchase over time with a generous intro APR window.",
    badge: null,
    recommended: "Freedom Unlimited",
  },
] as const;

const ChaseLogo: React.FC = () => (
  <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
    <svg width="28" height="28" viewBox="0 0 32 32" aria-hidden>
      <g fill="#fff">
        <rect x="13" y="0" width="6" height="13" rx="0.5" />
        <rect x="13" y="19" width="6" height="13" rx="0.5" />
        <rect x="0" y="13" width="13" height="6" rx="0.5" />
        <rect x="19" y="13" width="13" height="6" rx="0.5" />
      </g>
    </svg>
    <span
      style={{
        color: "#fff",
        fontWeight: 700,
        letterSpacing: "0.04em",
        fontSize: 18,
      }}
    >
      CHASE
    </span>
  </div>
);

const Header: React.FC = () => (
  <header
    style={{
      backgroundColor: CHASE_BLUE,
      padding: "14px 22px",
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
    }}
  >
    <ChaseLogo />
    <span style={{ color: "#cfe1f3", fontSize: 13 }}>Apply for a card</span>
  </header>
);

const ProgressBar: React.FC<{ active: StepKey }> = ({ active }) => (
  <div
    style={{
      backgroundColor: "#fff",
      borderBottom: `1px solid ${CHASE_BORDER}`,
      padding: "16px 22px 14px",
    }}
  >
    <div
      style={{
        fontSize: 12,
        color: CHASE_MUTED,
        marginBottom: 8,
        fontWeight: 600,
        letterSpacing: "0.06em",
        textTransform: "uppercase",
      }}
    >
      Step {active} of 5
    </div>
    <div style={{ display: "flex", gap: 6 }}>
      {STEPS.map((step) => {
        const filled = step.key <= active;
        return (
          <div
            key={step.key}
            style={{
              flex: 1,
              height: 4,
              borderRadius: 2,
              backgroundColor: filled ? CHASE_BLUE : "#E1E4E8",
            }}
          />
        );
      })}
    </div>
    <div
      style={{
        display: "flex",
        justifyContent: "space-between",
        marginTop: 8,
        fontSize: 11,
        color: CHASE_MUTED,
      }}
    >
      {STEPS.map((step) => (
        <span
          key={step.key}
          style={{
            color: step.key === active ? CHASE_DARK : CHASE_MUTED,
            fontWeight: step.key === active ? 600 : 400,
          }}
        >
          {step.label}
        </span>
      ))}
    </div>
  </div>
);

const Card: React.FC<
  React.PropsWithChildren<{ style?: React.CSSProperties }>
> = ({ children, style }) => (
  <div
    style={{
      backgroundColor: "#fff",
      border: `1px solid ${CHASE_BORDER}`,
      borderRadius: 10,
      padding: 22,
      ...style,
    }}
  >
    {children}
  </div>
);

const PrimaryButton: React.FC<
  React.PropsWithChildren<{
    onClick: () => void;
    disabled?: boolean;
  }>
> = ({ children, onClick, disabled }) => (
  <button
    type="button"
    disabled={disabled}
    onClick={onClick}
    style={{
      width: "100%",
      backgroundColor: disabled ? "#9DBBD9" : CHASE_BLUE,
      color: "#fff",
      border: "none",
      borderRadius: 8,
      padding: "13px 16px",
      fontSize: 15,
      fontWeight: 600,
      cursor: disabled ? "not-allowed" : "pointer",
      boxShadow: disabled ? "none" : "0 1px 0 rgba(0,0,0,0.04)",
    }}
  >
    {children}
  </button>
);

const SecondaryButton: React.FC<
  React.PropsWithChildren<{ onClick: () => void }>
> = ({ children, onClick }) => (
  <button
    type="button"
    onClick={onClick}
    style={{
      width: "100%",
      backgroundColor: "#fff",
      color: CHASE_DARK,
      border: `1px solid ${CHASE_DARK}`,
      borderRadius: 8,
      padding: "12px 16px",
      fontSize: 14,
      fontWeight: 600,
      cursor: "pointer",
    }}
  >
    {children}
  </button>
);

const Step1Goal: React.FC<{
  selected: string | null;
  onSelect: (id: string) => void;
  onNext: () => void;
}> = ({ selected, onSelect, onNext }) => (
  <div style={{ padding: "22px 22px 28px", display: "grid", gap: 14 }}>
    <h1
      style={{
        fontSize: 24,
        color: CHASE_TEXT,
        margin: 0,
        fontWeight: 700,
        lineHeight: 1.25,
      }}
    >
      What are you looking for in a card?
    </h1>
    <p style={{ color: CHASE_MUTED, fontSize: 14, margin: 0 }}>
      We&apos;ll match you with the Chase card that fits your goals best.
    </p>

    <div style={{ display: "grid", gap: 10, marginTop: 6 }}>
      {goalCards.map((card) => {
        const isSelected = selected === card.id;
        return (
          <button
            key={card.id}
            type="button"
            onClick={() => onSelect(card.id)}
            style={{
              textAlign: "left",
              padding: "16px 18px",
              borderRadius: 10,
              border: `1.5px solid ${isSelected ? CHASE_BLUE : CHASE_BORDER}`,
              backgroundColor: isSelected ? "#EEF6FE" : "#fff",
              cursor: "pointer",
              display: "grid",
              gap: 4,
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 8,
              }}
            >
              <span
                style={{
                  fontSize: 15,
                  fontWeight: 600,
                  color: CHASE_TEXT,
                }}
              >
                {card.title}
              </span>
              {card.badge ? (
                <span
                  style={{
                    fontSize: 10,
                    fontWeight: 700,
                    color: "#fff",
                    backgroundColor: CHASE_DARK,
                    padding: "3px 7px",
                    borderRadius: 999,
                    letterSpacing: "0.04em",
                  }}
                >
                  {card.badge.toUpperCase()}
                </span>
              ) : null}
            </div>
            <span style={{ fontSize: 13, color: CHASE_MUTED, lineHeight: 1.5 }}>
              {card.description}
            </span>
            <span
              style={{
                fontSize: 11,
                color: CHASE_BLUE,
                fontWeight: 600,
                marginTop: 4,
              }}
            >
              Recommended: {card.recommended}
            </span>
          </button>
        );
      })}
    </div>

    <div style={{ marginTop: 6 }}>
      <PrimaryButton onClick={onNext} disabled={!selected}>
        Continue
      </PrimaryButton>
    </div>
  </div>
);

const Field: React.FC<{
  label: string;
  value: string;
  hint?: string;
  badge?: string;
  type?: "text" | "tel";
}> = ({ label, value, hint, badge, type = "text" }) => (
  <div style={{ display: "grid", gap: 6 }}>
    <div
      style={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
      }}
    >
      <label
        style={{
          fontSize: 12,
          color: CHASE_MUTED,
          fontWeight: 600,
          letterSpacing: "0.04em",
          textTransform: "uppercase",
        }}
      >
        {label}
      </label>
      {badge ? (
        <span
          style={{
            fontSize: 10,
            fontWeight: 700,
            color: CHASE_DARK,
            backgroundColor: "#E6F0FA",
            padding: "3px 7px",
            borderRadius: 999,
            letterSpacing: "0.04em",
          }}
        >
          {badge.toUpperCase()}
        </span>
      ) : null}
    </div>
    <input
      type={type}
      defaultValue={value}
      style={{
        fontSize: 15,
        padding: "12px 14px",
        border: `1.5px solid ${CHASE_BORDER}`,
        borderRadius: 8,
        backgroundColor: "#fff",
        color: CHASE_TEXT,
      }}
    />
    {hint ? (
      <span style={{ fontSize: 11, color: CHASE_MUTED }}>{hint}</span>
    ) : null}
  </div>
);

const Step2Verify: React.FC<{
  onBack: () => void;
  onNext: () => void;
}> = ({ onBack, onNext }) => (
  <div style={{ padding: "22px 22px 28px", display: "grid", gap: 16 }}>
    <h1
      style={{
        fontSize: 22,
        color: CHASE_TEXT,
        margin: 0,
        fontWeight: 700,
        lineHeight: 1.3,
      }}
    >
      Confirm your details
    </h1>
    <p style={{ color: CHASE_MUTED, fontSize: 13, margin: 0 }}>
      We&apos;ve pre-filled what we already know from your Chase Premier
      Checking. Please confirm or edit before continuing.
    </p>

    <Card style={{ display: "grid", gap: 14 }}>
      <Field label="First name" value="Sarah" />
      <Field label="Last name" value="Chen" />
      <Field label="Date of birth" value="04 / 18 / 1990" />
      <Field
        label="Mobile phone"
        value="(312) 555-0144"
        type="tel"
        hint="We'll text a verification code."
      />
      <Field
        label="Annual income (pre-tax)"
        value="$98,400"
        badge="Auto-filled"
        hint="Verified from your Premier Checking on Apr 21. Looks wrong?"
      />
      <Field
        label="Last 4 of SSN"
        value="••••"
        hint="Soft pull only — won't affect your credit score."
      />
    </Card>

    <div
      style={{
        backgroundColor: "#F0F7FF",
        border: `1px solid #C9DEF2`,
        borderRadius: 10,
        padding: "12px 14px",
        fontSize: 12,
        color: CHASE_DARK,
        lineHeight: 1.5,
      }}
    >
      <strong>Why we ask.</strong> The information above is required by federal
      law (Truth-in-Lending Act). Your data is encrypted in transit and at rest.
    </div>

    <div style={{ display: "grid", gap: 10, gridTemplateColumns: "1fr 2fr" }}>
      <SecondaryButton onClick={onBack}>Back</SecondaryButton>
      <PrimaryButton onClick={onNext}>Continue</PrimaryButton>
    </div>
  </div>
);

const Step3Offer: React.FC<{
  onBack: () => void;
  onNext: () => void;
}> = ({ onBack, onNext }) => {
  const [showWhy, setShowWhy] = React.useState(false);
  return (
    <div style={{ padding: "22px 22px 28px", display: "grid", gap: 16 }}>
      <h1
        style={{
          fontSize: 22,
          color: CHASE_TEXT,
          margin: 0,
          fontWeight: 700,
          lineHeight: 1.3,
        }}
      >
        Your personalized offer
      </h1>
      <p style={{ color: CHASE_MUTED, fontSize: 13, margin: 0 }}>
        This is a soft inquiry — applying won&apos;t affect your credit score.
      </p>

      <div
        style={{
          background: `linear-gradient(135deg, ${CHASE_DARK} 0%, ${CHASE_BLUE} 100%)`,
          borderRadius: 14,
          padding: 22,
          color: "#fff",
        }}
      >
        <div style={{ fontSize: 12, opacity: 0.85, letterSpacing: "0.04em" }}>
          CHASE SAPPHIRE PREFERRED
        </div>
        <div
          style={{
            fontSize: 32,
            fontWeight: 700,
            marginTop: 6,
            letterSpacing: "-0.01em",
          }}
        >
          $24,000 limit
        </div>
        <div style={{ fontSize: 13, opacity: 0.9, marginTop: 4 }}>
          21.24% – 28.24% variable APR · 60,000 bonus points after $4,000 in
          purchases in the first 3 months
        </div>
      </div>

      <Card>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            cursor: "pointer",
          }}
          onClick={() => setShowWhy((current) => !current)}
        >
          <span style={{ fontSize: 14, fontWeight: 600, color: CHASE_TEXT }}>
            Why this offer?
          </span>
          <span style={{ fontSize: 13, color: CHASE_BLUE, fontWeight: 600 }}>
            {showWhy ? "Hide" : "See details"}
          </span>
        </div>
        {showWhy ? (
          <div
            style={{
              marginTop: 12,
              fontSize: 13,
              color: CHASE_MUTED,
              lineHeight: 1.55,
            }}
          >
            Based on your verified income, your existing Chase Premier Checking
            relationship, and a recent Equifax soft pull (FICO 9 score of 762),
            you qualify for the Sapphire Preferred at the $24,000 limit and the
            lowest published APR tier. The 60,000-point bonus is our current
            public offer for qualifying applicants — you can compare it to the
            Sapphire Reserve in the next step.
          </div>
        ) : null}
      </Card>

      <Card style={{ display: "grid", gap: 8 }}>
        <span style={{ fontSize: 13, fontWeight: 600, color: CHASE_TEXT }}>
          Compared to market average
        </span>
        <div style={{ display: "grid", gap: 6, marginTop: 4 }}>
          <Bar label="Your APR" value={24} max={36} highlight />
          <Bar label="Market average APR" value={28} max={36} />
          <Bar label="Your credit limit" value={24000} max={30000} highlight />
          <Bar label="Average new-account limit" value={11500} max={30000} />
        </div>
      </Card>

      <div style={{ display: "grid", gap: 10, gridTemplateColumns: "1fr 2fr" }}>
        <SecondaryButton onClick={onBack}>Back</SecondaryButton>
        <PrimaryButton onClick={onNext}>Accept and continue</PrimaryButton>
      </div>
    </div>
  );
};

const Bar: React.FC<{
  label: string;
  value: number;
  max: number;
  highlight?: boolean;
}> = ({ label, value, max, highlight }) => {
  const pct = Math.min(100, Math.round((value / max) * 100));
  const display =
    value > 1000 ? `$${value.toLocaleString()}` : `${value.toFixed(2)}%`;
  return (
    <div style={{ display: "grid", gap: 4 }}>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          fontSize: 12,
          color: CHASE_MUTED,
        }}
      >
        <span>{label}</span>
        <span
          style={{
            color: highlight ? CHASE_BLUE : CHASE_TEXT,
            fontWeight: 600,
          }}
        >
          {display}
        </span>
      </div>
      <div
        style={{
          height: 6,
          backgroundColor: "#EEF1F4",
          borderRadius: 3,
        }}
      >
        <div
          style={{
            width: `${pct}%`,
            height: "100%",
            backgroundColor: highlight ? CHASE_BLUE : "#9DA9B7",
            borderRadius: 3,
          }}
        />
      </div>
    </div>
  );
};

const Step4Terms: React.FC<{
  onBack: () => void;
  onNext: () => void;
  agreed: boolean;
  onAgreedChange: (next: boolean) => void;
}> = ({ onBack, onNext, agreed, onAgreedChange }) => {
  const sections = [
    {
      title: "Fees",
      summary: "$0 annual fee. 3% balance transfer fee. 5% cash advance fee.",
    },
    {
      title: "Interest rates",
      summary:
        "21.24% – 28.24% variable APR on purchases. 0% intro APR for 12 months on balance transfers, then variable rate applies.",
    },
    {
      title: "Penalties",
      summary:
        "Up to $40 late fee. Up to $40 returned payment fee. Penalty APR up to 29.99% may apply if you make a late payment.",
    },
    {
      title: "Rewards & bonus",
      summary:
        "Earn 2x points on dining and travel, 1x on everything else. 60,000 bonus points after $4,000 in purchases in your first 3 months. Points never expire while your account is open.",
    },
    {
      title: "Privacy & data",
      summary:
        "Chase will share your data internally for servicing and fraud prevention. We do not sell your personal data. Full privacy notice linked below.",
    },
  ];
  return (
    <div style={{ padding: "22px 22px 28px", display: "grid", gap: 16 }}>
      <h1
        style={{
          fontSize: 22,
          color: CHASE_TEXT,
          margin: 0,
          fontWeight: 700,
          lineHeight: 1.3,
        }}
      >
        Review your terms
      </h1>
      <p style={{ color: CHASE_MUTED, fontSize: 13, margin: 0 }}>
        Plain-language summaries below. Tap any section to read the full legal
        text.
      </p>

      <div style={{ display: "grid", gap: 10 }}>
        {sections.map((section) => (
          <Card
            key={section.title}
            style={{ padding: "14px 18px", display: "grid", gap: 4 }}
          >
            <span style={{ fontSize: 14, fontWeight: 600, color: CHASE_TEXT }}>
              {section.title}
            </span>
            <span
              style={{ fontSize: 12, color: CHASE_MUTED, lineHeight: 1.55 }}
            >
              {section.summary}
            </span>
          </Card>
        ))}
      </div>

      <label
        style={{
          display: "flex",
          alignItems: "flex-start",
          gap: 10,
          padding: "14px 16px",
          border: `1.5px solid ${agreed ? CHASE_BLUE : CHASE_BORDER}`,
          borderRadius: 10,
          backgroundColor: agreed ? "#EEF6FE" : "#fff",
          cursor: "pointer",
        }}
      >
        <input
          type="checkbox"
          checked={agreed}
          onChange={(event) => onAgreedChange(event.target.checked)}
          style={{
            marginTop: 2,
            width: 18,
            height: 18,
            accentColor: CHASE_BLUE,
            cursor: "pointer",
          }}
        />
        <span style={{ fontSize: 13, color: CHASE_TEXT, lineHeight: 1.5 }}>
          I have read and agree to the Chase Sapphire Preferred Cardholder
          Agreement, the Privacy Notice, and the e-Sign Consent. I authorize
          Chase to obtain a hard credit inquiry to finalize my application.
        </span>
      </label>

      <div style={{ display: "grid", gap: 10, gridTemplateColumns: "1fr 2fr" }}>
        <SecondaryButton onClick={onBack}>Back</SecondaryButton>
        <PrimaryButton onClick={onNext} disabled={!agreed}>
          Submit application
        </PrimaryButton>
      </div>
    </div>
  );
};

const Step5Done: React.FC<{ onRestart: () => void }> = ({ onRestart }) => (
  <div style={{ padding: "22px 22px 28px", display: "grid", gap: 18 }}>
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: 10,
        color: "#1F8B4D",
        fontSize: 14,
        fontWeight: 600,
      }}
    >
      <svg width="22" height="22" viewBox="0 0 24 24" aria-hidden>
        <circle cx="12" cy="12" r="11" fill="#1F8B4D" />
        <path
          d="M7 12.5l3 3 7-7"
          stroke="#fff"
          strokeWidth="2"
          fill="none"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
      Approved
    </div>
    <h1
      style={{
        fontSize: 24,
        color: CHASE_TEXT,
        margin: 0,
        fontWeight: 700,
        lineHeight: 1.25,
      }}
    >
      You&apos;re all set, Sarah.
    </h1>
    <p
      style={{ color: CHASE_MUTED, fontSize: 13, margin: 0, lineHeight: 1.55 }}
    >
      Your virtual card is ready to use right now. The physical card will arrive
      in 5–7 business days.
    </p>

    <div
      style={{
        background: `linear-gradient(135deg, ${CHASE_DARK} 0%, ${CHASE_BLUE} 60%, #1A8FE0 100%)`,
        borderRadius: 16,
        padding: 22,
        color: "#fff",
        boxShadow: "0 14px 40px -18px rgba(10, 79, 139, 0.6)",
      }}
    >
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <span
          style={{
            fontSize: 11,
            letterSpacing: "0.18em",
            opacity: 0.8,
          }}
        >
          CHASE SAPPHIRE PREFERRED
        </span>
        <span
          style={{
            fontSize: 11,
            letterSpacing: "0.18em",
            opacity: 0.8,
          }}
        >
          VISA
        </span>
      </div>
      <div
        style={{
          marginTop: 28,
          fontSize: 18,
          letterSpacing: "0.18em",
          fontVariantNumeric: "tabular-nums",
        }}
      >
        4147 ••• ••• 8821
      </div>
      <div
        style={{
          marginTop: 18,
          display: "flex",
          justifyContent: "space-between",
          fontSize: 12,
        }}
      >
        <div>
          <div style={{ opacity: 0.7, fontSize: 10, letterSpacing: "0.16em" }}>
            CARDHOLDER
          </div>
          <div style={{ marginTop: 4, fontWeight: 600 }}>SARAH CHEN</div>
        </div>
        <div>
          <div style={{ opacity: 0.7, fontSize: 10, letterSpacing: "0.16em" }}>
            EXPIRES
          </div>
          <div style={{ marginTop: 4, fontWeight: 600 }}>04/30</div>
        </div>
      </div>
    </div>

    <Card style={{ display: "grid", gap: 10 }}>
      <div style={{ display: "flex", justifyContent: "space-between" }}>
        <span style={{ fontSize: 12, color: CHASE_MUTED }}>Credit limit</span>
        <span style={{ fontSize: 13, color: CHASE_TEXT, fontWeight: 600 }}>
          $24,000
        </span>
      </div>
      <div style={{ display: "flex", justifyContent: "space-between" }}>
        <span style={{ fontSize: 12, color: CHASE_MUTED }}>APR</span>
        <span style={{ fontSize: 13, color: CHASE_TEXT, fontWeight: 600 }}>
          21.24% variable
        </span>
      </div>
      <div style={{ display: "flex", justifyContent: "space-between" }}>
        <span style={{ fontSize: 12, color: CHASE_MUTED }}>Welcome bonus</span>
        <span style={{ fontSize: 13, color: CHASE_TEXT, fontWeight: 600 }}>
          60,000 points
        </span>
      </div>
    </Card>

    <button
      type="button"
      onClick={() => undefined}
      style={{
        width: "100%",
        backgroundColor: "#000",
        color: "#fff",
        border: "none",
        borderRadius: 10,
        padding: "14px 16px",
        fontSize: 15,
        fontWeight: 600,
        cursor: "pointer",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        gap: 8,
      }}
    >
      <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden fill="#fff">
        <path d="M16.4 1.5c.3 1.6-.4 3.1-1.3 4.2-.9 1-2.4 1.8-3.9 1.7-.3-1.6.5-3.2 1.3-4.1 1-1 2.6-1.7 3.9-1.8zM20.7 17.5c-.6 1.4-1.4 2.7-2.4 3.9-1.3 1.5-3 2.6-4.6 2.6s-2-.9-3.7-.9c-1.7 0-2.2.9-3.7.9s-3.2-1.4-4.5-2.9c-2.7-3.1-3.3-9-1.4-13.1.9-2.1 2.6-3.4 4.4-3.4 1.5 0 2.4 1 3.7 1 1.2 0 2-1 3.7-1 1.7 0 3.4.9 4.5 2.5-3.9 2.2-3.3 7.7.9 8.4-.4.7-.6 1.3-.9 2z" />
      </svg>
      Add to Apple Wallet
    </button>

    <SecondaryButton onClick={onRestart}>Restart demo</SecondaryButton>

    <div
      style={{
        textAlign: "center",
        fontSize: 11,
        color: CHASE_MUTED,
        marginTop: 6,
        lineHeight: 1.6,
      }}
    >
      JPMorgan Chase Bank, N.A. Member FDIC. Equal Housing Lender. Demo — not a
      real application.
    </div>
  </div>
);

const PATH_TO_STEP: Record<string, StepKey> = {
  "": 1,
  goal: 1,
  verify: 2,
  offer: 3,
  terms: 4,
  done: 5,
};

const STEP_TO_PATH: Record<StepKey, string> = {
  1: "goal",
  2: "verify",
  3: "offer",
  4: "terms",
  5: "done",
};

const DEMO_BASE_PATH = "/demo/chase-credit-card-application";

const resolveStepFromPathname = (pathname: string): StepKey => {
  const remainder = pathname
    .replace(DEMO_BASE_PATH, "")
    .replace(/^\/+/, "")
    .replace(/\/+$/, "")
    .toLowerCase();
  return PATH_TO_STEP[remainder] ?? 1;
};

const ChaseCreditCardDemoPage: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [step, setStep] = React.useState<StepKey>(() =>
    resolveStepFromPathname(location.pathname),
  );
  const [goal, setGoal] = React.useState<string | null>("rewards");
  const [agreed, setAgreed] = React.useState(false);

  // Keep step in sync when the URL changes (e.g. when the parent tagging
  // workspace navigates the iframe via setting iframe.src).
  React.useEffect(() => {
    const next = resolveStepFromPathname(location.pathname);
    setStep((current) => (current === next ? current : next));
  }, [location.pathname]);

  // Reflect step → URL when the user clicks Continue / Back so the
  // browser address bar (and any external observer) stays consistent.
  const goToStep = React.useCallback(
    (next: StepKey) => {
      setStep(next);
      const targetPath = `${DEMO_BASE_PATH}/${STEP_TO_PATH[next]}`;
      if (location.pathname !== targetPath) {
        navigate(targetPath, { replace: true });
      }
    },
    [location.pathname, navigate],
  );

  const restart = () => {
    setGoal("rewards");
    setAgreed(false);
    goToStep(1);
  };

  return (
    <div
      style={{
        minHeight: "100vh",
        backgroundColor: CHASE_BG,
        fontFamily:
          "Inter, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
        color: CHASE_TEXT,
      }}
    >
      <Header />
      <ProgressBar active={step} />
      <div style={{ maxWidth: 520, margin: "0 auto" }}>
        {step === 1 ? (
          <Step1Goal
            selected={goal}
            onSelect={(id) => setGoal(id)}
            onNext={() => goToStep(2)}
          />
        ) : null}
        {step === 2 ? (
          <Step2Verify onBack={() => goToStep(1)} onNext={() => goToStep(3)} />
        ) : null}
        {step === 3 ? (
          <Step3Offer onBack={() => goToStep(2)} onNext={() => goToStep(4)} />
        ) : null}
        {step === 4 ? (
          <Step4Terms
            onBack={() => goToStep(3)}
            onNext={() => goToStep(5)}
            agreed={agreed}
            onAgreedChange={setAgreed}
          />
        ) : null}
        {step === 5 ? <Step5Done onRestart={restart} /> : null}
      </div>
    </div>
  );
};

export default ChaseCreditCardDemoPage;
